import { useState, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { authClient } from '../lib/auth-client'
import { Icon } from '../components/Common.jsx'
import AuthSide from '../components/AuthSide.jsx'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const linkError = params.get('error')

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')

  const initialError = useMemo(() => {
    if (linkError === 'invalid_token' || linkError === 'INVALID_TOKEN') {
      return 'This reset link is invalid. Request a new one below.'
    }
    if (linkError) return 'This reset link can no longer be used. Request a new one below.'
    if (!token) return 'Missing reset token. Use the link from your email.'
    return ''
  }, [linkError, token])

  const submit = async (e) => {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    if (!token) {
      setError('Missing reset token. Use the link from your email.')
      return
    }

    setSubmitting(true)
    try {
      const { error: authError } = await authClient.resetPassword({
        newPassword: password,
        token,
      })
      setSubmitting(false)
      if (authError) {
        setError(authError.message || 'Could not reset password. Request a new link.')
        return
      }
      setDone(true)
      setTimeout(() => navigate('/signin?reset=1'), 1500)
    } catch (err) {
      setSubmitting(false)
      setError('Could not reset password. Try again.')
    }
  }

  const blocked = !token || !!linkError

  return (
    <div className="auth-page">
      <AuthSide
        title="Choose a new password."
        subtitle="Pick something strong — at least 8 characters."
        stats={[{ v: 'Secure', l: 'Always' }, { v: '1 hour', l: 'Link valid' }, { v: '24/7', l: 'Support' }]}
        chatVariant="A"
      />
      <div className="auth-form-wrap">
        <form className="auth-form" onSubmit={submit}>
          <h1>Reset password</h1>
          <p>Enter a new password for your account</p>

          {(error || initialError) && (
            <div
              role="alert"
              style={{
                marginBottom: 16, padding: '10px 14px', borderRadius: 10, fontSize: 13,
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', color: '#F87171',
              }}
            >
              {error || initialError}
            </div>
          )}

          {done && (
            <div
              role="status"
              style={{
                marginBottom: 16, padding: '10px 14px', borderRadius: 10, fontSize: 13,
                background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.4)', color: '#10B981',
              }}
            >
              Password updated. Redirecting to sign in...
            </div>
          )}

          <div className="field">
            <label>New password</label>
            <div className="input-group">
              <Icon name="lock" size={16} />
              <input
                type={showPass ? 'text' : 'password'}
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                disabled={blocked || done}
              />
              <button
                type="button"
                className="input-suffix"
                onClick={() => setShowPass((s) => !s)}
              >
                <Icon name={showPass ? 'eye-off' : 'eye'} size={16} />
              </button>
            </div>
          </div>

          <div className="field">
            <label>Confirm password</label>
            <div className="input-group">
              <Icon name="lock" size={16} />
              <input
                type={showPass ? 'text' : 'password'}
                className="input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                disabled={blocked || done}
              />
            </div>
          </div>

          <button
            className="btn btn-primary"
            type="submit"
            disabled={submitting || done || blocked}
            style={{ width: '100%', justifyContent: 'center' }}
          >
            {submitting ? 'Updating...' : <>Update password <Icon name="send" size={16} /></>}
          </button>

          <div className="auth-foot">
            <Link to="/forgot-password">Request a new reset link</Link>
            {' · '}
            <Link to="/signin">
              <Icon name="arrow-left" size={13} style={{ display: 'inline', verticalAlign: -2 }} /> Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}
